import axios, { type AxiosProgressEvent } from 'axios';
import type { Project, Scene, PointCloudData, UploadResponse, UploadProgress } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120_000,
});

export async function uploadFile(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await apiClient.post<UploadResponse>('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (event.total && onProgress) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage,
        });
      }
    },
  });

  return data;
}

export async function fetchProjects(): Promise<Project[]> {
  const { data } = await apiClient.get<Project[]>('/projects');
  return data;
}

export async function fetchScene(sceneId: string): Promise<Scene> {
  const { data } = await apiClient.get<Scene>(`/scenes/${sceneId}`);
  return data;
}

export async function fetchPointCloud(sceneId: string): Promise<PointCloudData> {
  const { data } = await apiClient.get<PointCloudData>(`/scenes/${sceneId}/points`);
  return data;
}

export async function fetchMesh(sceneId: string): Promise<{ indices: Uint32Array; vertices: Float32Array }> {
  const { data } = await apiClient.get<{ indices: number[]; vertices: number[] }>(`/scenes/${sceneId}/mesh`);
  return {
    indices: new Uint32Array(data.indices),
    vertices: new Float32Array(data.vertices),
  };
}
